import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Stack,
  Button,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Checkbox,
  FormControlLabel,
  IconButton,
  Snackbar,
  Alert,
  Skeleton,
  CircularProgress,
  Chip,
} from '@mui/material';
import { Add, Delete, Visibility, PlayCircleOutline } from '@mui/icons-material';

import hrPlusService from '../../services/hrPlusService';
import {
  PageHeader,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  StatusDot,
  TypePill,
  ErrorState,
  TableEmptyState,
} from '../../components/common/pageKit';
import { formatIndianNumber, formatCurrency } from '../../utils/currency';

// ---------------------------------------------------------------------------
// Salary Structures — Payroll+ (migration 063, hrPayrollPlus model/controller).
// Component math (FLAT / PERCENT_OF_BASIC / PERCENT_OF_CTC / PERCENT_OF_GROSS)
// is computed server-side by previewStructure(); this page never re-derives
// the numbers itself, it only renders what the API returns.
// ---------------------------------------------------------------------------

type ComponentType = 'EARNING' | 'DEDUCTION';
type CalcType = 'FLAT' | 'PERCENT_OF_BASIC' | 'PERCENT_OF_CTC' | 'PERCENT_OF_GROSS';

interface SalaryComponent {
  id: number;
  structure_id: number;
  name: string;
  component_type: ComponentType;
  calc_type: CalcType;
  value: number | string;
  sequence: number;
  is_statutory: number | boolean;
  is_taxable: number | boolean;
}

interface SalaryStructureListItem {
  id: number;
  name: string;
  description: string | null;
  grade: string | null;
  is_active: number | boolean;
  component_count: number;
  created_at: string;
}

interface SalaryStructureDetail extends SalaryStructureListItem {
  components: SalaryComponent[];
}

interface PreviewLine {
  componentId: number;
  name: string;
  componentType: ComponentType;
  calcType: CalcType;
  value: number;
  monthlyAmount: number;
  annualAmount: number;
}

interface StructurePreview {
  structureId: number;
  structureName: string;
  ctcAnnual: number;
  monthlyCtc: number;
  components: PreviewLine[];
  basicMonthly: number;
  grossMonthly: number;
  grossAnnual: number;
  deductionsMonthly: number;
  deductionsAnnual: number;
  netMonthly: number;
  netAnnual: number;
}

const toNum = (v: any): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'string' ? parseFloat(v) : Number(v);
  return Number.isFinite(n) ? n : null;
};

const isTruthy = (v: any): boolean => v === 1 || v === true || v === '1';

/** Compact money, overflow-safe, full-precision on hover. */
const Money: React.FC<{ value: any; strong?: boolean }> = ({ value, strong }) => {
  const n = toNum(value);
  if (n === null) return <>—</>;
  return (
    <Typography
      component="span"
      title={formatCurrency(n)}
      sx={{
        fontVariantNumeric: 'tabular-nums',
        fontWeight: strong ? 700 : 400,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: 'inline-block',
        maxWidth: '100%',
      }}
    >
      {formatIndianNumber(n)}
    </Typography>
  );
};

const CALC_TYPES: CalcType[] = ['FLAT', 'PERCENT_OF_BASIC', 'PERCENT_OF_CTC', 'PERCENT_OF_GROSS'];
const CALC_TYPE_LABEL: Record<CalcType, string> = {
  FLAT: 'Flat Amount',
  PERCENT_OF_BASIC: '% of Basic',
  PERCENT_OF_CTC: '% of CTC',
  PERCENT_OF_GROSS: '% of Gross',
};

// ---------------------------------------------------------------------------
// Component builder row — used inside the New Structure dialog.
// ---------------------------------------------------------------------------

interface DraftComponent {
  key: string;
  name: string;
  componentType: ComponentType;
  calcType: CalcType;
  value: string;
  isStatutory: boolean;
}

const emptyDraftComponent = (): DraftComponent => ({
  key: `${Date.now()}-${Math.random()}`,
  name: '',
  componentType: 'EARNING',
  calcType: 'FLAT',
  value: '',
  isStatutory: false,
});

// ---------------------------------------------------------------------------
// New structure dialog
// ---------------------------------------------------------------------------

const NewStructureDialog: React.FC<{
  open: boolean;
  saving: boolean;
  onClose: () => void;
  onSubmit: (data: { name: string; description: string; grade: string; components: DraftComponent[] }) => void;
}> = ({ open, saving, onClose, onSubmit }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [grade, setGrade] = useState('');
  const [components, setComponents] = useState<DraftComponent[]>([emptyDraftComponent()]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName('');
      setDescription('');
      setGrade('');
      setComponents([emptyDraftComponent()]);
      setError(null);
    }
  }, [open]);

  const updateComponent = (key: string, patch: Partial<DraftComponent>) =>
    setComponents((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const addRow = () => setComponents((rows) => [...rows, emptyDraftComponent()]);
  const removeRow = (key: string) => setComponents((rows) => rows.filter((r) => r.key !== key));

  const handleSubmit = () => {
    if (!name.trim()) { setError('Structure name is required'); return; }
    const validComponents = components.filter((c) => c.name.trim());
    onSubmit({ name: name.trim(), description, grade, components: validComponents });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>New Salary Structure</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ mt: 0.25 }}>
          <Grid size={{ xs: 12, sm: 5 }}>
            <TextField
              label="Structure Name"
              required
              fullWidth
              size="small"
              value={name}
              error={!!error}
              helperText={error || ' '}
              onChange={(e) => { setName(e.target.value); if (e.target.value.trim()) setError(null); }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField label="Grade" fullWidth size="small" value={grade} onChange={(e) => setGrade(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField label="Description" fullWidth size="small" value={description} onChange={(e) => setDescription(e.target.value)} />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2.5 }} />

        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Components</Typography>
          <Button size="small" startIcon={<Add />} onClick={addRow}>Add Component</Button>
        </Stack>

        <Stack spacing={1.5}>
          {components.map((c) => (
            <Paper key={c.key} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
              <Grid container spacing={1.5} alignItems="center">
                <Grid size={{ xs: 12, sm: 3 }}>
                  <TextField label="Name" size="small" fullWidth value={c.name} onChange={(e) => updateComponent(c.key, { name: e.target.value })} />
                </Grid>
                <Grid size={{ xs: 6, sm: 2 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel id={`ctype-${c.key}`}>Type</InputLabel>
                    <Select
                      labelId={`ctype-${c.key}`}
                      label="Type"
                      value={c.componentType}
                      onChange={(e) => updateComponent(c.key, { componentType: e.target.value as ComponentType })}
                    >
                      <MenuItem value="EARNING">Earning</MenuItem>
                      <MenuItem value="DEDUCTION">Deduction</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel id={`calc-${c.key}`}>Calc Type</InputLabel>
                    <Select
                      labelId={`calc-${c.key}`}
                      label="Calc Type"
                      value={c.calcType}
                      onChange={(e) => updateComponent(c.key, { calcType: e.target.value as CalcType })}
                    >
                      {CALC_TYPES.map((t) => <MenuItem key={t} value={t}>{CALC_TYPE_LABEL[t]}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 6, sm: 2 }}>
                  <TextField
                    label={c.calcType === 'FLAT' ? 'Value (₹/mo)' : 'Value (%)'}
                    type="number"
                    size="small"
                    fullWidth
                    value={c.value}
                    onChange={(e) => updateComponent(c.key, { value: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 4, sm: 1.5 }}>
                  <FormControlLabel
                    control={<Checkbox size="small" checked={c.isStatutory} onChange={(e) => updateComponent(c.key, { isStatutory: e.target.checked })} />}
                    label={<Typography variant="caption">Statutory</Typography>}
                  />
                </Grid>
                <Grid size={{ xs: 2, sm: 0.5 }}>
                  <IconButton size="small" onClick={() => removeRow(c.key)} disabled={components.length === 1}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Grid>
              </Grid>
            </Paper>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>{saving ? 'Saving…' : 'Create Structure'}</Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Structure detail dialog — components breakdown + live CTC preview
// ---------------------------------------------------------------------------

const StructureDetailDialog: React.FC<{
  open: boolean;
  loading: boolean;
  structure: SalaryStructureDetail | null;
  onClose: () => void;
  onPreview: (ctc: number) => Promise<void>;
  preview: StructurePreview | null;
  previewLoading: boolean;
}> = ({ open, loading, structure, onClose, onPreview, preview, previewLoading }) => {
  const [ctc, setCtc] = useState('');

  useEffect(() => {
    if (open) setCtc('');
  }, [open, structure?.id]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{structure ? structure.name : 'Salary Structure'}</DialogTitle>
      <DialogContent dividers>
        {loading || !structure ? (
          <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>
        ) : (
          <Stack spacing={3}>
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
              {structure.grade && <TypePill label={structure.grade} tint="#7c3aed" />}
              <StatusDot label={isTruthy(structure.is_active) ? 'Active' : 'Inactive'} tone={isTruthy(structure.is_active) ? 'success' : 'neutral'} />
              {structure.description && <Typography variant="body2" color="text.secondary">{structure.description}</Typography>}
            </Stack>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Components</Typography>
              {structure.components.length === 0 ? (
                <TableEmptyState title="No components defined" />
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={headerCellSx}>Name</TableCell>
                        <TableCell sx={headerCellSx}>Type</TableCell>
                        <TableCell sx={headerCellSx}>Calc</TableCell>
                        <TableCell sx={headerCellSx} align="right">Value</TableCell>
                        <TableCell sx={headerCellSx}>Statutory</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {structure.components.map((c) => (
                        <TableRow key={c.id} sx={bodyRowSx}>
                          <TableCell sx={{ fontWeight: 600 }}>{c.name}</TableCell>
                          <TableCell><TypePill label={c.component_type} tint={c.component_type === 'EARNING' ? '#059669' : '#dc2626'} /></TableCell>
                          <TableCell>{CALC_TYPE_LABEL[c.calc_type]}</TableCell>
                          <TableCell align="right">{c.calc_type === 'FLAT' ? <Money value={c.value} /> : `${toNum(c.value) ?? 0}%`}</TableCell>
                          <TableCell>{isTruthy(c.is_statutory) ? <Chip size="small" label="Statutory" /> : '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Live Preview</Typography>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <TextField
                  label="Annual CTC"
                  type="number"
                  size="small"
                  value={ctc}
                  onChange={(e) => setCtc(e.target.value)}
                  sx={{ maxWidth: 220 }}
                />
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<PlayCircleOutline />}
                  disabled={!ctc || Number(ctc) <= 0 || previewLoading}
                  onClick={() => onPreview(Number(ctc))}
                >
                  {previewLoading ? 'Computing…' : 'Preview'}
                </Button>
              </Stack>

              {preview && (
                <Stack spacing={2}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={headerCellSx}>Component</TableCell>
                          <TableCell sx={headerCellSx}>Type</TableCell>
                          <TableCell sx={headerCellSx} align="right">Monthly</TableCell>
                          <TableCell sx={headerCellSx} align="right">Annual</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {preview.components.map((l) => (
                          <TableRow key={l.componentId} sx={bodyRowSx}>
                            <TableCell sx={{ fontWeight: 600 }}>{l.name}</TableCell>
                            <TableCell><TypePill label={l.componentType} tint={l.componentType === 'EARNING' ? '#059669' : '#dc2626'} /></TableCell>
                            <TableCell align="right"><Money value={l.monthlyAmount} /></TableCell>
                            <TableCell align="right"><Money value={l.annualAmount} /></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="caption" color="text.secondary">Gross (Monthly)</Typography>
                      <Box><Money value={preview.grossMonthly} strong /></Box>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="caption" color="text.secondary">Deductions (Monthly)</Typography>
                      <Box><Money value={preview.deductionsMonthly} strong /></Box>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="caption" color="text.secondary">Net (Monthly)</Typography>
                      <Box><Money value={preview.netMonthly} strong /></Box>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="caption" color="text.secondary">Net (Annual)</Typography>
                      <Box><Money value={preview.netAnnual} strong /></Box>
                    </Grid>
                  </Grid>
                </Stack>
              )}
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const SalaryStructuresPage: React.FC = () => {
  const [structures, setStructures] = useState<SalaryStructureListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  const [newOpen, setNewOpen] = useState(false);
  const [newSaving, setNewSaving] = useState(false);

  const [detailId, setDetailId] = useState<number | null>(null);
  const [detail, setDetail] = useState<SalaryStructureDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [preview, setPreview] = useState<StructurePreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await hrPlusService.getSalaryStructures();
      setStructures(r.structures || []);
    } catch (e: any) {
      setError(e.message || 'Failed to load salary structures');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const activeCount = useMemo(() => structures.filter((s) => isTruthy(s.is_active)).length, [structures]);

  const handleCreate = async (data: { name: string; description: string; grade: string; components: DraftComponent[] }) => {
    setNewSaving(true);
    try {
      await hrPlusService.createSalaryStructure({
        name: data.name,
        description: data.description || undefined,
        grade: data.grade || undefined,
        components: data.components.map((c, i) => ({
          name: c.name.trim(),
          componentType: c.componentType,
          calcType: c.calcType,
          value: Number(c.value) || 0,
          sequence: i,
          isStatutory: c.isStatutory,
        })),
      });
      setToast({ msg: 'Salary structure created', severity: 'success' });
      setNewOpen(false);
      await load();
    } catch (e: any) {
      setToast({ msg: e.message || 'Failed to create structure', severity: 'error' });
    } finally {
      setNewSaving(false);
    }
  };

  const openDetail = async (id: number) => {
    setDetailId(id);
    setDetail(null);
    setPreview(null);
    setDetailLoading(true);
    try {
      const r = await hrPlusService.getSalaryStructure(id);
      setDetail(r.structure || null);
    } catch (e: any) {
      setToast({ msg: e.message || 'Failed to load structure', severity: 'error' });
    } finally {
      setDetailLoading(false);
    }
  };

  const handlePreview = async (ctc: number) => {
    if (!detailId) return;
    setPreviewLoading(true);
    try {
      const p = await hrPlusService.previewStructure(detailId, ctc);
      setPreview(p || null);
    } catch (e: any) {
      setToast({ msg: e.message || 'Failed to compute preview', severity: 'error' });
    } finally {
      setPreviewLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width={220} height={40} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={44} sx={{ mb: 2, borderRadius: 2 }} />
        <Skeleton variant="rounded" height={360} sx={{ borderRadius: 3 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <PageHeader title="Salary Structures" subtitle="Payroll+ component builder with live CTC preview" />
        <TableSurface><ErrorState message={error} onRetry={load} /></TableSurface>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Salary Structures"
        subtitle={`${structures.length} structure${structures.length === 1 ? '' : 's'} · ${activeCount} active`}
        actions={
          <Button variant="contained" startIcon={<Add />} onClick={() => setNewOpen(true)}>
            New Structure
          </Button>
        }
      />

      <TableSurface>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>Name</TableCell>
                <TableCell sx={headerCellSx}>Grade</TableCell>
                <TableCell sx={headerCellSx} align="right">Components</TableCell>
                <TableCell sx={headerCellSx}>Status</TableCell>
                <TableCell sx={headerCellSx} align="right">View</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {structures.length === 0 ? (
                <TableRow><TableCell colSpan={5} sx={{ border: 0 }}><TableEmptyState title="No salary structures" hint="Create one to start building CTC breakdowns." /></TableCell></TableRow>
              ) : (
                structures.map((s) => (
                  <TableRow key={s.id} hover sx={{ ...bodyRowSx, cursor: 'pointer' }} onClick={() => openDetail(s.id)}>
                    <TableCell sx={{ fontWeight: 600 }}>{s.name}</TableCell>
                    <TableCell>{s.grade || '—'}</TableCell>
                    <TableCell align="right">{s.component_count}</TableCell>
                    <TableCell><StatusDot label={isTruthy(s.is_active) ? 'Active' : 'Inactive'} tone={isTruthy(s.is_active) ? 'success' : 'neutral'} /></TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); openDetail(s.id); }}>
                        <Visibility fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TableSurface>

      <NewStructureDialog open={newOpen} saving={newSaving} onClose={() => setNewOpen(false)} onSubmit={handleCreate} />

      <StructureDetailDialog
        open={detailId !== null}
        loading={detailLoading}
        structure={detail}
        onClose={() => { setDetailId(null); setDetail(null); setPreview(null); }}
        onPreview={handlePreview}
        preview={preview}
        previewLoading={previewLoading}
      />

      <Snackbar open={!!toast} autoHideDuration={3200} onClose={() => setToast(null)}>
        {toast ? <Alert severity={toast.severity} variant="filled" onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
};

export default SalaryStructuresPage;
